import { Dispatch } from '@reduxjs/toolkit'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { INTRODUCING_BROKERS_CACHE_TTL_MS } from 'utils/cache'

import { AppState } from '../index'
import { isQAMode } from '../store'
import { partnershipsSlice } from './partnerships.slice'

export { INTRODUCING_BROKERS_CACHE_TTL_MS } from 'utils/cache'

const introducingBrokersRequestsByCompany = new Map<
  number,
  ReturnType<PartnershipsApiClient['getIntroducingBrokersByTickmillCompany']>
>()

export const fetchIntroducingBrokerStatusesAction = (
  partnershipsApiClient: PartnershipsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await partnershipsApiClient.getIntroducingBrokerStatus()
      dispatch(partnershipsSlice.actions.setIntroducingBrokerStatus(data))
    } catch (e) {
      console.error('Cannot get introducing broker types.', e)
    }
  }
}

export const fetchIntroducingBrokerCalculationStatusesAction = (
  partnershipsApiClient: PartnershipsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await partnershipsApiClient.getIntroducingBrokerCalculationStatus()
      dispatch(partnershipsSlice.actions.setIntroducingBrokerCalculationStatus(data))
    } catch (e) {
      console.error('Cannot get introducing broker calculation status.', e)
    }
  }
}

export const fetchIntroducingBrokerProfileAccountTypeAction = (
  partnershipsApiClient: PartnershipsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await partnershipsApiClient.getIntroducingBrokerProfileAccountType()
      dispatch(partnershipsSlice.actions.setIntroducingBrokerProfileAccountType(data))
    } catch (e) {
      console.error('Cannot get introducing broker calculation status.', e)
    }
  }
}

export const fetchIntroducingBrokerParentTypesAction = (
  partnershipsApiClient: PartnershipsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await partnershipsApiClient.getIntroducingBrokerParentTypes()
      dispatch(partnershipsSlice.actions.setIntroducingBrokerParentTypes(data))
    } catch (e) {
      console.error('Cannot get introducing broker parent types.', e)
    }
  }
}

export const fetchBonusNvAdjustmentReasons = (partnershipsApiClient: PartnershipsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await partnershipsApiClient.getBonusNvAdjustmentReasons()
      dispatch(partnershipsSlice.actions.setBonusNvAdjustmentReasons(data))
    } catch (e) {
      console.error('Cannot get platform types', e)
    }
  }
}

export const fetchRebateCalcMethodTypesAction = (partnershipsApiClient: PartnershipsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await partnershipsApiClient.getRebateCalcMethodType()
      dispatch(partnershipsSlice.actions.setRebateCalcMethodTypes(data))
    } catch (e) {
      console.error('Cannot get rebate calc method types', e)
    }
  }
}

export const fetchIntroducingBrokerByTickmillCompany = (
  partnershipsApiClient: PartnershipsApiClient,
  companyId: number
) => {
  return async (dispatch: Dispatch, getState: () => AppState): Promise<void> => {
    try {
      const state = getState()
      const cachedData = state.partnerships?.introducingBrokersByCompany?.[companyId]
      const cachedAt = state.partnerships?.introducingBrokersCachedAtByCompany?.[companyId]
      const isCacheValid =
        Array.isArray(cachedData) &&
        cachedData.length > 0 &&
        typeof cachedAt === 'number' &&
        Date.now() - cachedAt < INTRODUCING_BROKERS_CACHE_TTL_MS

      if (!isQAMode() && isCacheValid) {
        return
      }

      const pendingRequest = introducingBrokersRequestsByCompany.get(companyId)
      if (pendingRequest) {
        await pendingRequest
        return
      }

      const request = partnershipsApiClient.getIntroducingBrokersByTickmillCompany(companyId)
      introducingBrokersRequestsByCompany.set(companyId, request)

      try {
        const response = await request
        dispatch(
          partnershipsSlice.actions.setIntroducingBrokersByTickmillCompany({
            tickmillCompanyId: companyId,
            data: response,
            cachedAt: Date.now(),
          })
        )
      } finally {
        introducingBrokersRequestsByCompany.delete(companyId)
      }
    } catch (event) {
      console.error('Cannot get IB.', event)
    }
  }
}
export const fetchIntroducingBrokerProfilesByTickmillCompany = (
  partnershipsApiClient: PartnershipsApiClient,
  id: number
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response =
        await partnershipsApiClient.getIntroducingBrokerProfileByTickmillCompanyId(id)
      dispatch(
        partnershipsSlice.actions.setIntroducingBrokersProfilesByTickmillCompany(response.items)
      )
    } catch (e) {
      console.error('Cannot get IB.', e)
    }
  }
}

export const fetchIBRebatesApprovalTypes = (partnershipsApiClient: PartnershipsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const ibRebatesApprovalTypes = await partnershipsApiClient.getIBRebatesApprovalTypes()
      dispatch(partnershipsSlice.actions.setIBRebatesApprovalTypes(ibRebatesApprovalTypes))
    } catch (e) {
      console.error('Error during get wallet restrictions', e)
    }
  }
}

export const fetchIBRebatesPeriods = (partnershipsApiClient: PartnershipsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const ibRebatesPeriods = await partnershipsApiClient.getIBRebatesPeriods()
      dispatch(partnershipsSlice.actions.setIBRebatesPeriods(ibRebatesPeriods))
    } catch (e) {
      console.error('Error during get wallet restrictions', e)
    }
  }
}
